/* Hallmark · component: Calendar · genre: modern-minimal · theme: maeiz-tokens */
import React, { useMemo, useState } from "react";
import { clsx } from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function parseISO(iso?: string): Date | null {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

function toISO(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function sameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export interface CalendarProps {
    value?: string;
    onChange?: (iso: string) => void;
    /** Inclusive range highlight when both set */
    rangeStart?: string;
    rangeEnd?: string;
    min?: string;
    max?: string;
    className?: string;
}

export const Calendar: React.FC<CalendarProps> = ({
    value,
    onChange,
    rangeStart,
    rangeEnd,
    min,
    max,
    className,
}) => {
    const selected = parseISO(value);
    const start = parseISO(rangeStart);
    const end = parseISO(rangeEnd);
    const minD = parseISO(min);
    const maxD = parseISO(max);
    const initial = selected ?? start ?? new Date();
    const [cursor, setCursor] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), 1));

    const days = useMemo(() => {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();
        const first = new Date(year, month, 1);
        const startPad = first.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells: Array<{ date: Date; inMonth: boolean } | null> = [];
        for (let i = 0; i < startPad; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: new Date(year, month, d), inMonth: true });
        }
        return cells;
    }, [cursor]);

    const monthLabel = cursor.toLocaleString("en", { month: "long", year: "numeric" });
    const today = new Date();

    return (
        <div className={clsx("w-[280px] select-none p-1", className)}>
            <div className="mb-2 flex items-center justify-between px-1">
                <button
                    type="button"
                    className="rounded-lg p-1.5 text-primary hover:bg-primary/10 dark:text-gold-light dark:hover:bg-gold/15"
                    aria-label="Previous month"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                >
                    <ChevronLeft size={16} />
                </button>
                <p className="text-sm font-semibold text-neutral-dark dark:text-white">{monthLabel}</p>
                <button
                    type="button"
                    className="rounded-lg p-1.5 text-primary hover:bg-primary/10 dark:text-gold-light dark:hover:bg-gold/15"
                    aria-label="Next month"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
            <div className="mb-1 grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((d) => (
                    <div key={d} className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
                {days.map((cell, i) => {
                    if (!cell) return <div key={`e-${i}`} />;
                    const { date } = cell;
                    const iso = toISO(date);
                    const isSelected = selected ? sameDay(date, selected) : false;
                    const isStart = start ? sameDay(date, start) : false;
                    const isEnd = end ? sameDay(date, end) : false;
                    const inRange =
                        start && end && date.getTime() > start.getTime() && date.getTime() < end.getTime();
                    const disabled =
                        Boolean((minD && date.getTime() < minD.getTime()) || (maxD && date.getTime() > maxD.getTime()));
                    const isToday = sameDay(date, today);

                    return (
                        <button
                            key={iso}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange?.(iso)}
                            className={clsx(
                                "relative flex h-9 items-center justify-center rounded-lg text-sm tabular-nums",
                                "disabled:pointer-events-none disabled:opacity-30",
                                isSelected || isStart || isEnd
                                    ? "bg-primary font-semibold text-white hover:bg-primary-dark dark:bg-gold dark:text-neutral-ink dark:hover:bg-gold-dark"
                                    : clsx(
                                          "text-neutral-dark hover:bg-primary/10 dark:text-white dark:hover:bg-gold/15",
                                          inRange && "bg-primary/10 dark:bg-gold/15",
                                          isToday &&
                                              "ring-1 ring-inset ring-primary/40 dark:ring-gold/50",
                                      ),
                            )}
                        >
                            {date.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export { toISO as dateToISO, parseISO as parseISODate };
